<!-- ❗Errors in the form are set on line 60 -->
<script setup>
import { VForm } from 'vuetify/components/VForm'
import authV1BottomShape from '@images/svg/auth-v1-bottom-shape.svg?raw'
import authV1TopShape from '@images/svg/auth-v1-top-shape.svg?raw'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import $api from "~/utils/api";
import LoginApprovalHandler from '~/components/LoginApprovalHandler.vue'

const { t } = useI18n()

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const isPasswordVisible = ref(false)
const router = useRouter()
const ability = useAbility()

const errors = ref({
  email: undefined,
  password: undefined,
})

const errorMessage = ref(null)

const refVForm = ref()
const isLoading = ref(false)
const showApprovalHandler = ref(false)
const approvalRequestId = ref(null)

const credentials = ref({
  email: '',
  password: '',
  remember: false,
})

// Notifications
const notification = ref({
  show: false,
  type: 'success', // success, error, warning, info
  title: '',
  message: '',
  timeout: 2000
})


// Notification functions
const showNotification = (type, title, message, timeout = 5000) => {
  notification.value = {
    show: true,
    type,
    title,
    message,
    timeout
  }

  // Auto hide after timeout
  setTimeout(() => {
    notification.value.show = false
  }, timeout)
}

const hideNotification = () => {
  notification.value.show = false
}

const login = async () => {
  try {
    isLoading.value = true
    errors.value = { email: undefined, password: undefined }
    errorMessage.value = null

    // Ensure credentials is properly initialized
    if (!credentials.value) {
      credentials.value = {
        email: '',
        password: '',
        remember: false,
      }
    }

    const res = await $api.post('/auth/login', {
      email: credentials.value.email,
      password: credentials.value.password,
      remember: credentials.value.remember,
    })

    // Проверяем, нужна ли авторизация администратора
    if (res.data.requires_approval) {
      approvalRequestId.value = res.data.request_id
      showApprovalHandler.value = true
      return
    }

    // Сохраняем данные в localStorage
    localStorage.setItem('userAbilityRules', JSON.stringify(res.data.data.user.abilityRules))
    localStorage.setItem('userData', JSON.stringify(res.data.data.user))
    localStorage.setItem('accessToken', res.data.data.token)

    // Обновляем ability
    ability.update(res.data.data.user.abilityRules)

    router.push('/jobs')
  } catch (error) {

    if (error.response?.status === 422) {
      // Обработка ошибок валидации
      const validationErrors = error.response.data.errors || {}
      errorMessage.value = error.response.data.message
      errors.value = validationErrors

      if (errorMessage.value) {
        showNotification('error', t('login.validation_error'), errorMessage.value)
      }
    } else if (error.response?.status === 401) {
      // Обработка ошибки авторизации
      errors.value = { email: [t('login.invalid_credentials')] }
      showNotification('error', t('login.auth_error'), t('login.invalid_credentials'))
    } else if (error.response?.status === 429) {
      // Обработка ошибки слишком много попыток
      showNotification('error', t('login.too_many_attempts'), t('login.try_again_later'))
    } else if (error.response?.status >= 500) {
      // Обработка серверных ошибок
      showNotification('error', t('login.server_error'), t('login.server_error_message'))
    } else {
      // Обработка других ошибок
      const errorMessage = error.response?.data?.message || error.message || t('login.unknown_error')
      showNotification('error', t('login.login_error'), errorMessage)
    }
  } finally {
    isLoading.value = false
  }
}

const handleApprovalComplete = (res) => {
  showApprovalHandler.value = false
  approvalRequestId.value = null

  // Сохраняем данные в localStorage
  localStorage.setItem('userAbilityRules', JSON.stringify(res.data.user.abilityRules))
  localStorage.setItem('userData', JSON.stringify(res.data.user))
  localStorage.setItem('accessToken', res.data.token)

  ability.update(res.data.user.abilityRules)

  // Перенаправляем на главную страницу после успешной авторизации
  router.push('/jobs')
}

const handleApprovalClose = () => {
  showApprovalHandler.value = false
  approvalRequestId.value = null
}

const handleApprovalError = (error) => {
  console.error(t('login.approval_error'), error)
  showApprovalHandler.value = false
  approvalRequestId.value = null
  // Можно показать toast с ошибкой
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid)
      login()
  })
}

// Ensure credentials is initialized on mount
onMounted(() => {
  if (!credentials.value) {
    credentials.value = {
      email: '',
      password: '',
      remember: false,
    }
  }
})
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <div class="position-relative my-sm-16">
      <!-- 👉 Top shape -->
      <VNodeRenderer :nodes="h('div', { innerHTML: authV1TopShape })"
        class="text-primary auth-v1-top-shape d-none d-sm-block" />

      <!-- 👉 Bottom shape -->
      <VNodeRenderer :nodes="h('div', { innerHTML: authV1BottomShape })"
        class="text-primary auth-v1-bottom-shape d-none d-sm-block" />

      <!-- 👉 Auth Card -->
      <VCard class="auth-card pa-4" max-width="380" width="380">
        <VCardItem class="justify-center">
          <template #prepend>
            <div class="d-flex">
              <VNodeRenderer :nodes="themeConfig.app.logoBlack" />
            </div>
          </template>
        </VCardItem>

        <VCardText>
          <VForm ref="refVForm" @submit.prevent="onSubmit">
            <VRow>

              <!-- email -->
              <VCol cols="12">
                <AppTextField v-model="credentials.email" :label="t('login.email')"
                  :placeholder="t('login.email_placeholder')" type="email" autofocus
                  :rules="[requiredValidator, emailValidator]" :error-messages="errors.email" />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField v-model="credentials.password" :label="t('login.password')"
                  :placeholder="t('login.password_placeholder')" :rules="[requiredValidator]"
                  :type="isPasswordVisible ? 'text' : 'password'" :error-messages="errors.password"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible" />

                <!-- remember me checkbox -->
                <div class="d-flex align-center justify-space-between flex-wrap mt-2 mb-4">
                  <VCheckbox v-model="credentials.remember" :label="t('login.remember_me')" />
                </div>

                <!-- login button -->
                <VBtn block type="submit" :loading="isLoading" :disabled="isLoading">
                  {{ isLoading ? t('login.loading') : t('login.login_button') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </div>
    <!-- Notification Snackbar -->
    <VSnackbar v-model="notification.show"
      :color="notification.type === 'error' ? 'error' : notification.type === 'warning' ? 'warning' : notification.type === 'info' ? 'info' : 'success'"
      :timeout="notification.timeout" location="top end" variant="elevated">
      <div class="d-flex align-center">
        <VIcon :icon="notification.type === 'error' ? 'tabler-alert-circle' :
          notification.type === 'warning' ? 'tabler-alert-triangle' :
            notification.type === 'info' ? 'tabler-info-circle' :
              'tabler-check-circle'" class="me-3" />
        <div>
          <div class="font-weight-bold">{{ notification.title }}</div>
          <div class="text-caption">{{ notification.message }}</div>
        </div>
      </div>

      <template #actions>
        <VBtn icon="tabler-x" size="small" variant="text" @click.stop="hideNotification" />
      </template>
    </VSnackbar>
    <!-- Login Approval Handler -->
    <LoginApprovalHandler v-if="showApprovalHandler" :request-id="approvalRequestId" :email="credentials?.email || ''"
      :password="credentials?.password || ''" @success="handleApprovalComplete" @error="handleApprovalError"
      @close="handleApprovalClose" />
  </div>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth.scss";
</style>
